import { ReactElement } from 'react';
import Head from 'next/head';
import { Button } from '@eventbrite/marmalade';
import { FormattedMessage, useIntl } from 'react-intl';
import { Stage } from '@eventbrite/webapp-constants';
import { AssetsPrefetchLinks, FavIconLinks, MetaTags } from '@eventbrite/webapp-public-content';

import { type ErrorResponse, errorMessages } from './errorMessages';
import { GenericErrorIllustration } from './GenericErrorIllustration';
import styles from './ErrorPage.module.scss';

export function ErrorPageContent({
  error = {
    message: errorMessages.genericError.message.id,
    description: errorMessages.genericError.description.id,
  },
}: {
  error?: Omit<ErrorResponse, 'statusCode'>;
}): ReactElement {
  const handleClick = () => {
    window.location.href = 'https://www.eventbrite.com';
  };

  return (
    <div className={styles.errorPageBody} data-testid="error-page">
      <GenericErrorIllustration />
      <div className={styles.errorPageContent}>
        <h2 className={styles.errorPageTitle}>
          <FormattedMessage id={error.message} defaultMessage={error.message} />
        </h2>
        <p className={styles.errorPageDescription}>
          <FormattedMessage id={error.description} defaultMessage={error.description} />
        </p>
      </div>
      <Button id="browse-more-events-button" onClick={handleClick} role="button" variant="primary">
        <FormattedMessage id="Browse more events" defaultMessage="Browse more events" />
      </Button>
    </div>
  );
}

export function GenericErrorPage({
  error = {
    message: errorMessages.genericError.message.id,
    description: errorMessages.genericError.description.id,
  },
  stage,
}: {
  error?: Omit<ErrorResponse, 'statusCode'>;
  stage: Stage;
}): ReactElement {
  const { formatMessage } = useIntl();

  const title = formatMessage({ id: error.message, defaultMessage: error.message });
  const description = formatMessage({
    id: error.description,
    defaultMessage: error.description,
  });

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="noindex, nofollow" />
        <AssetsPrefetchLinks />
        <FavIconLinks stage={stage} />
        <MetaTags />
      </Head>
      <main>
        <ErrorPageContent error={error} />
      </main>
    </>
  );
}
